import { useEffect } from "react";
import { Box, Checkbox, Radio, TextField, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ReactComponent as CheckBoxUncheckedIcon } from "../../asserts/logo/Check_box.svg";
import { minContractEndDate, todayISODate, validateContractEnd } from "../../utils/contractDateValidation";

const FONT = "Lato, sans-serif";
const RATIONALE_MAX = 1000;
const EXCEPTION_TEXT_MAX = 500;
const EXCEPTION_TEXT_MIN = 10;
const EXCEPTION_MIN_MSG = "Please enter at least 10 characters.";
const EXCEPTION_MAX_MSG = "Maximum 500 characters allowed.";
const EXCEPTION_SPACE_MSG = "Remove unnecessary spaces.";
const EXCEPTION_VAGUE_MSG = "Please provide a clear, actionable description.";
const EXCEPTION_OK_MSG = "Looks good.";

/** Short words the spec treats as unclear even when they are under 10 characters, such as "Check". */
const SHORT_VAGUE = new Set([
  "check", "ok", "okay", "yes", "no", "na", "n/a", "tbd", "todo", "asap", "test", "none", "nil",
  "xxx", "abc", "idk", "wip", "tba", "tbc", "fix", "pass", "fail", "good", "bad", "fine", "later",
  "soon", "dummy", "temp", "pending",
]);

const VAGUE_EXACT = new Set([
  ...SHORT_VAGUE,
  "do it later", "do this later", "fix it", "fix later", "will do", "not sure", "maybe", "something",
  "whatever", "nothing", "handle it", "handle later", "look into it", "we'll see", "we will see",
  "see later", "update later", "to be done", "to be decided", "not applicable", "as discussed",
  "as needed", "follow up", "follow up later", "on hold", "testing", "placeholder", "temporary",
]);

const GENERIC_WORDS = new Set([
  "a", "an", "the", "and", "or", "to", "of", "for", "in", "on", "at", "by", "with", "from", "into",
  "over", "after", "before", "as", "is", "are", "be", "been", "being", "it", "its", "this", "that",
  "these", "those", "we", "our", "you", "your", "will", "shall", "should", "can", "may", "must",
  "do", "does", "did", "done", "later", "soon", "now", "then", "just", "only", "also", "not", "no",
  "yes", "ok", "okay", "please", "need", "needs", "needed", "make", "made", "get", "got", "have",
  "has", "had", "if", "when", "until", "up", "out", "about", "within", "into", "than", "per",
]);

function isClearDescription(trimmed) {
  const normalized = trimmed.toLowerCase().replace(/\s+/g, " ");
  if (VAGUE_EXACT.has(normalized)) return false;
  const specific = normalized
    .split(" ")
    .filter((word) => word.length >= 4 && !GENERIC_WORDS.has(word) && !/^\d+$/.test(word));
  return specific.length >= 3;
}

function exceptionTextStatus(value) {
  const raw = String(value ?? "");
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, message: "", counter: "muted" };
  if (raw.length > EXCEPTION_TEXT_MAX) return { ok: false, message: EXCEPTION_MAX_MSG, counter: "error" };
  if (raw !== trimmed) return { ok: false, message: EXCEPTION_SPACE_MSG, counter: "error" };
  const normalized = trimmed.toLowerCase().replace(/\s+/g, " ");
  if (SHORT_VAGUE.has(normalized)) return { ok: false, message: EXCEPTION_VAGUE_MSG, counter: "error" };
  if (trimmed.length < EXCEPTION_TEXT_MIN) return { ok: false, message: EXCEPTION_MIN_MSG, counter: "error" };
  if (!isClearDescription(trimmed)) return { ok: false, message: EXCEPTION_VAGUE_MSG, counter: "error" };
  return { ok: true, message: EXCEPTION_OK_MSG, counter: "ok" };
}

function DecisionCheckBoxCheckedIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" fill="#005BF0" stroke="#005BF0" />
      <path d="M5.25 9L7.75 11.5L12.75 6.5" stroke="#FFFFFF" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const VOTES = [
  { value: "go", title: "Go", detail: "All checklist items complete, no flags active." },
  { value: "conditional_go", title: "Conditional Go", detail: "All checklist items complete, no flags active." },
  { value: "no_go", title: "No-Go", detail: "A hard no-go condition is active. Cannot launch." },
];

const ROLES = [
  { title: "Head of Quality", detail: "Decision owner", badge: "Accountable" },
  { title: "Risk & Compliance", badge: "Consulted" },
  { title: "Security / Privacy", badge: "Consulted" },
  { title: "Ops / SRE", badge: "Consulted" },
];

export function decisionAuthorityAccountable() {
  return ROLES.find((role) => role.badge === "Accountable")?.title || "—";
}

export function decisionAuthorityConsulted() {
  const names = ROLES.filter((role) => role.badge === "Consulted").map((role) => role.title);
  return names.length ? names.join(", ") : "—";
}

const FIELD_SX = {
  width: "100%",
  boxSizing: "border-box",
  height: { xs: 40, sm: 38, lg: 36 },
  border: "1px solid #BBBBBB",
  borderRadius: "4px",
  px: { xs: "10px", lg: "12px" },
  fontFamily: FONT,
  fontWeight: 400,
  fontSize: { xs: 13, sm: 13, lg: 14 },
  lineHeight: { xs: "20px", lg: "22px" },
  color: "#383838",
  bgcolor: "#FFFFFF",
  outline: "none",
  "&::placeholder": { color: "#8D8D8D", opacity: 1 },
  "&:focus": { borderColor: "#005BF0" },
};

const EXCEPTION_LABEL_SX = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: { xs: 13, sm: 13, lg: 14 },
  lineHeight: { xs: "18px", sm: "20px", lg: "21px" },
  letterSpacing: 0,
  color: "#707070",
};

const GATE3_EXPIRY_DATE_SX = {
  mt: "8px",
  "& .MuiOutlinedInput-root": {
    height: { xs: 40, sm: 36, lg: 32 },
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    boxShadow: "inset 0px 2px 3px 1px #55555533",
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: { xs: 14, sm: 15, lg: 16 },
    lineHeight: { xs: "20px", sm: "22px", lg: "24px" },
    color: "#383838",
    "& fieldset": { borderColor: "#707070" },
    "&:hover fieldset": { borderColor: "#707070" },
    "&.Mui-focused fieldset": { borderColor: "#707070", borderWidth: "1px" },
    "&.Mui-error fieldset": { borderColor: "#D92D20" },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: { xs: 11, sm: 12, lg: 12 },
    lineHeight: { xs: "16px", lg: "18px" },
    letterSpacing: 0,
    mx: 0,
    mt: "8px",
  },
};

/** Go requires every applicable Readiness Checklist item to be complete. */
function isChecklistComplete(checklist, customDays, traceRequired) {
  const values = Object.entries(checklist || {});
  if (!values.length) return false;
  return values.every(([id, raw]) => {
    if (id === "trace" && !traceRequired) return true;
    const value = String(raw || "").trim();
    if (!value) return false;
    if (id === "cadence" && value === "Custom recurring day") return Number(customDays) > 0;
    return true;
  });
}

const EXCEPTION_FIELD_SX = {
  width: "100%",
  boxSizing: "border-box",
  height: { xs: 40, sm: 36, lg: 32 },
  mt: "8px",
  border: "1px solid #707070",
  borderRadius: "4px",
  px: "10px",
  py: "4px",
  bgcolor: "#FFFFFF",
  boxShadow: "inset 0px 2px 3px 1px #55555533",
  fontFamily: FONT,
  fontWeight: 400,
  fontSize: { xs: 14, sm: 15, lg: 16 },
  lineHeight: { xs: "20px", sm: "22px", lg: "24px" },
  letterSpacing: 0,
  color: "#383838",
  outline: "none",
  "&::placeholder": { color: "#707070", opacity: 1 },
  "&:focus": { borderColor: "#707070" },
};

function ExceptionTextField({ id, label, value, placeholder, onChange }) {
  const status = exceptionTextStatus(value);
  const showFeedback = Boolean(status.message);
  const border = !showFeedback ? "1px solid #707070" : status.ok ? "1px solid #12B76A" : "1px solid #D92D20";
  const counterColor = !showFeedback || status.counter === "muted" ? "#707070" : status.counter === "ok" ? "#027A48" : "#D92D20";
  return (
    <Box>
      <Typography component="label" htmlFor={id} sx={EXCEPTION_LABEL_SX}>
        <Box component="span" sx={{ color: "#D92D20", mr: 0.25 }} aria-hidden>
          *
        </Box>
        {label}
      </Typography>
      <Box
        component="textarea"
        id={id}
        value={value}
        placeholder={placeholder}
        aria-invalid={showFeedback && !status.ok}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          ...EXCEPTION_FIELD_SX,
          height: "auto",
          minHeight: { xs: 72, lg: 64 },
          maxHeight: 96,
          resize: "none",
          overflow: "auto",
          display: "block",
          border,
        }}
      />
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mt: "6px" }}>
        {showFeedback ? (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "6px", minWidth: 0 }}>
            {status.ok ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#027A48", mt: "1px", flexShrink: 0 }} />
            ) : (
              <ErrorOutlineIcon sx={{ fontSize: 16, color: "#D92D20", mt: "1px", flexShrink: 0 }} />
            )}
            <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: 12, lineHeight: "18px", color: status.ok ? "#027A48" : "#D92D20" }}>
              {status.message}
            </Typography>
          </Box>
        ) : (
          <Box />
        )}
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: 12, lineHeight: "18px", color: counterColor, flexShrink: 0 }}>
          {String(value || "").length}/{EXCEPTION_TEXT_MAX}
        </Typography>
      </Box>
    </Box>
  );
}

function Pill({ children, color, bgcolor, border }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        height: { xs: 20, sm: 22, lg: 24 },
        px: { xs: "8px", lg: "10px" },
        borderRadius: "12px",
        border,
        bgcolor,
        color,
        fontFamily: FONT,
        fontWeight: 600,
        fontSize: { xs: 11, sm: 11, lg: 12 },
        lineHeight: { xs: "20px", lg: "24px" },
        letterSpacing: 0,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

function SectionCard({ children, sx }) {
  return (
    <Box
      sx={{
        border: "1px solid #E6E6E6",
        borderRadius: { xs: "8px", lg: "12px" },
        bgcolor: "#FFFFFF",
        p: { xs: 1.5, sm: 2, lg: 2.5 },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function Gate3Decision({
  onReady,
  vote = "conditional_go",
  onVoteChange,
  exception = { condition: "", control: "", expiry: "" },
  onExceptionChange,
  rationale = "",
  onRationaleChange,
  attested = false,
  onAttestedChange,
  checklist = {},
  customDays = "",
  traceRequired = false,
}) 
{
  const goEnabled = isChecklistComplete(checklist, customDays, traceRequired);
  const condition = exception.condition;
  const control = exception.control;
  const expiry = exception.expiry;
  const patchException = (partial) => onExceptionChange?.({ ...exception, ...partial });
  const rationaleRequired = vote === "conditional_go" || vote === "no_go";
  const expiryRequired = vote === "conditional_go";
  const expiryMinDate = minContractEndDate(todayISODate());
  const expiryError = expiry ? validateContractEnd(expiry, todayISODate()) : null;
  const exceptionTextOk =
    vote !== "conditional_go" ||
    (exceptionTextStatus(condition).ok && exceptionTextStatus(control).ok);
  const canRecord =
    attested &&
    (vote !== "go" || goEnabled) &&
    (!rationaleRequired || rationale.trim() !== "") &&
    exceptionTextOk &&
    (!expiryRequired || (Boolean(expiry) && !validateContractEnd(expiry, todayISODate())));

  useEffect(() => {
    onReady?.(canRecord);
  }, [onReady, canRecord]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, sm: 1.5, lg: 2 }, width: "100%", maxWidth: { xs: "100%", lg: 1012 } }}>
      <SectionCard
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          columnGap: { xs: 1.25, sm: 1.5, lg: 2 },
          rowGap: { xs: 1, sm: 1.25, lg: 1.5 },
          py: { xs: 1.25, sm: 1.5, lg: 1.75 },
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: { xs: 1, sm: 1.25, lg: 1.5 }, minWidth: 0, flex: "1 1 220px" }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, sm: 13, lg: 14 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#383838" }}>
            Evaluation Outcome
          </Typography>
          <Box
            component="span"
            sx={{
              boxSizing: "border-box",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: { xs: "auto", lg: 97 },
              height: { xs: 18, sm: 20, lg: 20 },
              px: "8px",
              gap: "2px",
              borderRadius: "12px",
              border: "1px solid #8A5C00",
              bgcolor: "#FFF8E0",
              color: "#8A5C00",
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: { xs: 11, sm: 12, lg: 12 },
              lineHeight: 1,
              letterSpacing: 0,
              whiteSpace: "nowrap",
            }}
          >
            Conditional Go
          </Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, sm: 13, lg: 14 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#707070" }}>
            The application may proceed under a logged condition.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: { xs: "wrap", sm: "nowrap" }, alignItems: "center", flex: { xs: "1 1 100%", sm: "0 0 auto" }, gap: { xs: 1, sm: 0 } }}>
          {[
            ["0", "Hard Blockers"],
            ["1", "Open Gap"],
            ["1", "Required Exception"],
          ].map(([count, label], index) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center" }}>
              {index > 0 ? (
                <Box aria-hidden sx={{ display: { xs: "none", sm: "block" }, width: "1px", height: 16, bgcolor: "#E6E6E6", mx: { sm: 1.25, lg: 1.75 } }} />
              ) : null}
              <Typography component="span" sx={{ fontFamily: FONT, letterSpacing: 0, whiteSpace: "nowrap" }}>
                <Box component="span" sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 15, lg: 16 }, lineHeight: { xs: 1.15, lg: 1 }, color: "#383838" }}>
                  {count}
                </Box>
                <Box component="span" sx={{ fontWeight: 400, fontSize: { xs: 12, sm: 13, lg: 14 }, lineHeight: { xs: 1.15, lg: 1 }, color: "#707070" }}>
                  {` ${label}`}
                </Box>
              </Typography>
            </Box>
          ))}
        </Box>
      </SectionCard>

      <SectionCard sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, lg: 1.5 }, p: 0, py: { xs: 1.5, lg: 2 } }}>
        <Box sx={{ px: "16px" }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, sm: 14, lg: 14 }, lineHeight: { xs: "18px", lg: "21px" }, letterSpacing: 0, color: "#383838" }}>
            Decision Authority
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, sm: 13, lg: 13 }, lineHeight: { xs: "16px", lg: "18px" }, letterSpacing: 0, color: "#707070", mt: 0.5 }}>
            Decision permissions are defined by the Gate 3 RACI.
          </Typography>
        </Box>

        <Box>
          {ROLES.map((role) => {
            const isConsulted = role.badge === "Consulted";
            return (
            <Box
              key={role.title}
              sx={{
                boxSizing: "border-box",
                width: "100%",
                height: isConsulted ? { xs: "auto", lg: 44 } : "auto",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: { xs: 0.75, sm: 1.5 },
                px: "16px",
                py: "12px",
                borderBottom: "1px solid #E6E6E6",
              }}
            >
              <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, sm: 13, lg: 14 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#383838" }}>
                  {role.title}
                </Typography>
                {role.detail ? (
                  <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 13, sm: 13, lg: 14 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#005BF0" }}>
                    {role.detail}
                  </Typography>
                ) : null}
              </Box>
              {role.badge === "Accountable" ? (
                <Box
                  component="span"
                  sx={{
                    boxSizing: "border-box",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: { xs: "auto", lg: 83 },
                    height: 18,
                    px: "8px",
                    gap: "2px",
                    borderRadius: "12px",
                    border: "1px solid #005BF0",
                    bgcolor: "#F0F6FF",
                    color: "#005BF0",
                    fontFamily: FONT,
                    fontWeight: 700,
                    fontSize: { xs: 11, lg: 12 },
                    lineHeight: "24px",
                    letterSpacing: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  Accountable
                </Box>
              ) : (
                <Box
                  component="span"
                  sx={{
                    boxSizing: "border-box",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: { xs: "auto", lg: 71 },
                    height: { xs: 18, lg: 20 },
                    px: "8px",
                    gap: "2px",
                    borderRadius: "12px",
                    border: "1px solid #E6E6E6",
                    bgcolor: "#FFFFFF",
                    color: "#383838",
                    fontFamily: FONT,
                    fontWeight: 700,
                    fontSize: { xs: 11, lg: 12 },
                    lineHeight: 1,
                    letterSpacing: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  Consulted
                </Box>
              )}
            </Box>
            );
          })}
        </Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 13, sm: 13, lg: 14 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#707070", px: "16px" }}>
          Only the Accountable role can record the verdict.
        </Typography>
      </SectionCard>

      <SectionCard sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, lg: 1.25 } }}>
        <Box role="radiogroup" aria-label="Gate 3 verdict" sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, lg: 1.25 } }}>
          {VOTES.map((option) => {
            const selected = vote === option.value;
            const disabled = option.value === "go" && !goEnabled;
            return (
              <Box
                key={option.value}
                component="button"
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                aria-disabled={disabled}
                onClick={() => {
                  if (!disabled) onVoteChange?.(option.value);
                }}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: { xs: 1, lg: 1.5 },
                  width: "100%",
                  textAlign: "left",
                  p: { xs: 1.25, sm: 1.5, lg: 2 },
                  borderRadius: { xs: "6px", lg: "8px" },
                  border: "1px solid",
                  borderColor: selected ? "#005BF0" : "#E6E6E6",
                  bgcolor: selected ? "#F0F6FF" : "#FFFFFF",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.55 : 1,
                  boxSizing: "border-box",
                  "&:focus": { outline: "none" },
                  "&:focus-visible": { borderColor: "#005BF0" },
                }}
              >
                <Radio checked={selected} tabIndex={-1} aria-hidden sx={{ p: 0, color: "#707070", "&.Mui-checked": { color: "#005BF0" } }} />
                <Box>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, sm: 13, lg: 14 }, lineHeight: { xs: "18px", sm: "20px", lg: "21px" }, letterSpacing: 0, color: selected ? "#005BF0" : "#383838" }}>
                    {option.title}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, sm: 13, lg: 14 }, lineHeight: { xs: "18px", sm: "20px", lg: "21px" }, letterSpacing: 0, color: "#707070", mt: 0.25 }}>
                    {option.detail}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box>
          <Typography component="label" htmlFor="gate3-rationale" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, sm: 13, lg: 14 }, lineHeight: { xs: "18px", sm: "20px", lg: "21px" }, letterSpacing: 0, color: "#707070" }}>
            {rationaleRequired ? (
              <Box component="span" sx={{ color: "#D92D20", mr: 0.25 }} aria-hidden>
                *
              </Box>
            ) : null}
            Rationale
          </Typography>
          <Box
            component="textarea"
            id="gate3-rationale"
            value={rationale}
            maxLength={RATIONALE_MAX}
            required={rationaleRequired}
            aria-required={rationaleRequired}
            placeholder="Explain your decision..."
            onChange={(e) => onRationaleChange?.(e.target.value.slice(0, RATIONALE_MAX))}
            sx={{
              ...FIELD_SX,
              height: { xs: 88, sm: 80, lg: 72 },
              py: "8px",
              mt: 0.75,
              resize: "vertical",
              fontSize: { xs: 14, sm: 15, lg: 16 },
              lineHeight: { xs: "20px", sm: "22px", lg: "24px" },
              color: "#383838",
              "&::placeholder": { color: "#707070", opacity: 1, fontSize: "inherit", lineHeight: "inherit" },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mt: 0.5 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 11, sm: 12, lg: 12 }, lineHeight: { xs: "16px", lg: "18px" }, letterSpacing: 0, color: "#707070" }}>
              Rationale (required for Conditional Go or No-Go)
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 11, sm: 12, lg: 12 }, lineHeight: { xs: "16px", lg: "18px" }, letterSpacing: 0, color: "#707070", textAlign: "right", flexShrink: 0, ml: "auto" }}>
              {rationale.length}/{RATIONALE_MAX}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Checkbox
            checked={attested}
            onChange={(e) => onAttestedChange?.(e.target.checked)}
            disableRipple
            icon={<CheckBoxUncheckedIcon style={{ width: "100%", height: "100%", display: "block" }} />}
            checkedIcon={<DecisionCheckBoxCheckedIcon style={{ width: "100%", height: "100%", display: "block" }} />}
            inputProps={{ "aria-label": "I confirm all evidence is accurate and complete to the best of my knowledge." }}
            sx={{ p: 0, width: { xs: 18, sm: 18, lg: 24 }, height: { xs: 18, sm: 18, lg: 24 }, flexShrink: 0 }}
          />
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 11, sm: 12, lg: 12 }, lineHeight: { xs: "16px", lg: "18px" }, letterSpacing: 0, color: "#383838" }}>
            I confirm all evidence is accurate and complete to the best of my knowledge.
          </Typography>
        </Box>
      </SectionCard>

      {vote === "conditional_go" ? (
      <SectionCard sx={{ bgcolor: "#F7F7F7", display: "flex", flexDirection: "column", gap: { xs: "12px", sm: "12px", lg: "16px" } }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 14, sm: 15, lg: 16 }, lineHeight: { xs: 1.25, lg: 1 }, letterSpacing: 0, color: "#383838" }}>
            Post-launch sampling plan defined - logged exception
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 13, sm: 13, lg: 14 }, lineHeight: { xs: 1.3, lg: 1 }, letterSpacing: 0, textTransform: "capitalize", color: "#707070" }}>
            A condition, a compensating control, and an expiry — three different things, all required.
          </Typography>
        </Box>

        <ExceptionTextField
          id="gate3-condition"
          label="Condition"
          value={condition}
          placeholder="e.g., Launch proceeds with post-launch plan documented within 5 business days"
          onChange={(next) => patchException({ condition: next })}
        />
        <ExceptionTextField
          id="gate3-control"
          label="Compensating Control"
          value={control}
          placeholder="e.g., Manual weekly review by QA lead until automated sampling is configured"
          onChange={(next) => patchException({ control: next })}
        />
        <Box>
          <Typography component="label" htmlFor="gate3-expiry" sx={EXCEPTION_LABEL_SX}>
            <Box component="span" sx={{ color: "#D92D20", mr: 0.25 }} aria-hidden>
              *
            </Box>
            Expiry Date
          </Typography>
          <TextField
            id="gate3-expiry"
            fullWidth
            type="date"
            value={expiry}
            onChange={(e) => patchException({ expiry: e.target.value })}
            error={Boolean(expiry && expiryError)}
            helperText={
              expiry && expiryError
                ? expiryError
                : "Expiry date must be set to a valid upcoming business cycle date."
            }
            FormHelperTextProps={{ sx: { color: "#707070", "&.Mui-error": { color: "#D92D20" } } }}
            inputProps={{ min: expiryMinDate }}
            sx={GATE3_EXPIRY_DATE_SX}
          />
        </Box>
      </SectionCard>
      ) : null}
    </Box>
  );
}
